import { RepeatWrapping, type CanvasTexture, type Texture } from 'three';
import { SHOP_SIGNS } from '../../content/signs';
import { paintCompoundWall, paintConcreteOffice, paintGlass, paintShopFront, paintShophouse } from './facadePainters';

export type FacadeKind = 'glass' | 'concrete' | 'shophouse' | 'shopfront' | 'wall';

const VARIANTS: Record<FacadeKind, number> = { glass: 5, concrete: 4, shophouse: 8, shopfront: SHOP_SIGNS.length, wall: 4 };
const GLASS_TINTS = ['#3b6e8f', '#2f6f64', '#27465a', '#5b7f95', '#6b5a48'];
const CONCRETE = ['#dedbd3', '#d3cec2', '#c7c2b6', '#dbd3c1'];
const PAINT = ['#d6c08a', '#c9936a', '#b56a47', '#8fadb8', '#e2dccd', '#c8a294', '#a3b894', '#d3cdb4'];
const WALLS = ['#e4ddcf', '#d8cbb0', '#ddd0b6', '#cfd4d3'];

const cache = new Map<string, CanvasTexture>();

function paint(kind: FacadeKind, variant: number): CanvasTexture {
  const seed = variant * 97 + kind.length;
  switch (kind) {
    case 'glass':
      return paintGlass(GLASS_TINTS[variant % GLASS_TINTS.length] ?? '#3b6e8f', seed);
    case 'concrete':
      return paintConcreteOffice(CONCRETE[variant % CONCRETE.length] ?? '#eee', seed);
    case 'shophouse':
      return paintShophouse(PAINT[variant % PAINT.length] ?? '#f2d479', seed);
    case 'shopfront': {
      const sign = SHOP_SIGNS[variant % SHOP_SIGNS.length];
      if (!sign) throw new Error('no shop signs');
      return paintShopFront(sign, seed);
    }
    case 'wall':
      return paintCompoundWall(WALLS[variant % WALLS.length] ?? '#eee', seed);
  }
}

/** Metres of frontage one copy of the texture covers. Shop fronts and walls are painted as long strips. */
export function moduleWidth(kind: FacadeKind): number {
  return kind === 'shopfront' || kind === 'wall' ? 12 : 3;
}

export function variantCount(kind: FacadeKind): number {
  return VARIANTS[kind];
}

/** A texture for one building face, sharing the painted image with every other building of that look. */
export function facadeTexture(kind: FacadeKind, variant: number, repeatX: number, repeatY: number): Texture {
  const key = `${kind}:${variant % VARIANTS[kind]}`;
  let base = cache.get(key);
  if (!base) {
    base = paint(kind, variant % VARIANTS[kind]);
    cache.set(key, base);
  }
  const texture = base.clone();
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.repeat.set(repeatX, repeatY);
  return texture;
}
